import { surveyWidgetToRegular } from 'components/Chat/ChatFooter/SurveyWidget/utils';
import { InputWidgetData } from 'interfaces/InputWidgetData';
import { Action } from 'redux';

import {
    InputWidgetActionType,
    InputWidgetReplyButtonPressedAction,
    ReceiveInputWidgetAction,
    ReceiveSurveyWidgetAction,
    ResetInputWidgetOtpTimestampAction,
    RevealInputWidgetElementErrorsAction,
    SetInputWidgetButtonEnabledAction,
    SetInputWidgetButtonHiddenAction,
    SetInputWidgetElementErrorsAction,
    SetInputWidgetOtpInProgressAction,
    UpdateInputWidgetElementAction,
} from './inputWidget.actions';

export interface InputWidgetState extends InputWidgetData {
    event: InputWidgetActionType;
    evtPayload?: string;
    lastOtpTimestamp?: number;
    otpInProgress?: boolean;
    isSurvey?: boolean;
}

const initialState: InputWidgetState = null;

export const inputWidgetReducer = (
    state = initialState,
    action: Action<InputWidgetActionType>,
): typeof initialState => {
    switch (action.type) {
        case InputWidgetActionType.Receive:
            return {
                event: InputWidgetActionType.Receive,
                ...(action as ReceiveInputWidgetAction).payload,
            };

        case InputWidgetActionType.Close:
            return null;

        case InputWidgetActionType.UpdateElement: {
            if (!state) return null;

            const { elementId, elementData } = (action as UpdateInputWidgetElementAction).payload;
            const elementIdx = state.elements?.findIndex(
                (e) =>
                    e.elementId === elementId ||
                    (e.dynamicElementId && e.dynamicElementId === elementData?.dynamicElementId),
            );

            if (elementIdx === -1) return state;

            const newElements = [...state.elements];
            newElements[elementIdx] = { ...elementData };

            return {
                ...state,
                event: InputWidgetActionType.UpdateElement,
                elements: newElements,
            };
        }

        case InputWidgetActionType.SetElementErrors: {
            if (!state) return null;

            const { elementId, errors, revealNow } = (action as SetInputWidgetElementErrorsAction)
                .payload;

            const elementIdx = state.elements?.findIndex((e) => e.elementId === elementId);

            if (elementIdx === -1) return state;

            const newElements = [...state.elements];
            newElements[elementIdx] = { ...state.elements[elementIdx], errors };

            if (revealNow) {
                newElements[elementIdx].revealErrors = true;
            }

            return {
                ...state,
                event: InputWidgetActionType.SetElementErrors,
                elements: newElements,
            };
        }

        case InputWidgetActionType.SetButtonEnabled: {
            if (!state) return null;

            const { type, enabled } = (action as SetInputWidgetButtonEnabledAction).payload;
            const buttonIdx = state.buttons?.findIndex((b) => b.action === type);

            if (buttonIdx === -1) return state;

            const newButtons = [...state.buttons];
            newButtons[buttonIdx] = { ...newButtons[buttonIdx], disabled: !enabled };

            return {
                ...state,
                event: InputWidgetActionType.SetButtonEnabled,
                buttons: newButtons,
            };
        }

        case InputWidgetActionType.SetButtonHidden: {
            if (!state) return null;

            const { type, hidden } = (action as SetInputWidgetButtonHiddenAction).payload;
            const buttonIdx = state.buttons?.findIndex((b) => b.action === type);

            if (buttonIdx === -1) return state;

            const newButtons = [...state.buttons];
            newButtons[buttonIdx] = { ...newButtons[buttonIdx], hidden };

            return {
                ...state,
                event: InputWidgetActionType.SetButtonHidden,
                buttons: newButtons,
            };
        }

        case InputWidgetActionType.ResetOtpTimestamp: {
            if (!state) return null;

            const lastOtpTimestamp = (action as ResetInputWidgetOtpTimestampAction).payload;

            return {
                ...state,
                event: InputWidgetActionType.ResetOtpTimestamp,
                lastOtpTimestamp,
            };
        }

        case InputWidgetActionType.SetOtpInProgress: {
            if (!state) return null;

            const otpInProgress = (action as SetInputWidgetOtpInProgressAction).payload;

            return {
                ...state,
                event: InputWidgetActionType.SetOtpInProgress,
                otpInProgress,
            };
        }

        case InputWidgetActionType.ReplyButtonPressed: {
            const payload = (action as InputWidgetReplyButtonPressedAction).payload;
            return {
                ...state,
                event: InputWidgetActionType.ReplyButtonPressed,
                evtPayload: payload,
            };
        }

        case InputWidgetActionType.RevealElementErrors: {
            if (!state) return null;

            const elementId = (action as RevealInputWidgetElementErrorsAction).payload;
            const elementIdx = state.elements?.findIndex((e) => e.elementId === elementId);

            if (elementIdx === -1) return state;

            const elementData = state.elements[elementIdx];

            const newElements = [...state.elements];
            newElements[elementIdx] = { ...elementData, revealErrors: true };

            return {
                ...state,
                event: InputWidgetActionType.RevealElementErrors,
                elements: newElements,
            };
        }

        case InputWidgetActionType.ReceiveSurvey: {
            const command = (action as ReceiveSurveyWidgetAction).payload;

            return {
                event: InputWidgetActionType.Receive,
                ...surveyWidgetToRegular(command),
            };
        }

        default:
            return state;
    }
};
